import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { styled } from "goober";
import type { OmitProperties, PickProperties } from "ts-essentials";

import { useInertTransition } from "@/shared/Transition.jsx";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import { StrictEventEmitter } from "@/util/strict-event-emitter.mjs";
import { useSharedScope } from "@/util/use-shared-scope.mjs";

type RoutingDirection = "set" | "push" | "pop" | "replace";

type RouteMap = Record<PrimitiveKey, unknown>;

type RouteEntry<T extends RouteMap> = {
  [K in keyof T]: {
    name: K;
    param: T[K];
  };
}[keyof T];

type ModalNavigationRouterEvents<Routes extends RouteMap> = {
  routeChanged(
    prev: RouteEntry<Routes>,
    next: RouteEntry<Routes>,
    direction: RoutingDirection,
  ): void;
  replaceRoute(prev: RouteEntry<Routes>, next: RouteEntry<Routes>): void;

  pushRoute(next: RouteEntry<Routes>): void;
  popRoute(next: RouteEntry<Routes>): void;
};

export class ModalNavigationRouter<
  const Routes extends RouteMap = RouteMap,
> extends StrictEventEmitter<ModalNavigationRouterEvents<Routes>> {
  name: string;

  #currentRoute: RouteEntry<Routes>;
  #routeStack: RouteEntry<Routes>[] = [];

  constructor(name: string, initialRoute: RouteEntry<Routes>) {
    super();

    this.name = name;

    this.#currentRoute = initialRoute;
  }

  get currentRoute() {
    return this.#currentRoute;
  }

  get routeStack() {
    return this.#routeStack;
  }

  setRoute<K extends keyof PickProperties<Routes, void | never>>(name: K);

  setRoute<K extends keyof PickOptional<Routes>>(name: K, param?: Routes[K]);

  setRoute<K extends keyof OmitProperties<Routes, void | never>>(
    name: K,
    param: Routes[K],
  );

  /**
   * Sets the current route and clears the route stack. This removes all history,
   * so the user cannot go back to the previous route.
   */
  setRoute<K extends keyof Routes>(name: K, param?: Routes[K]): void {
    if (
      this.#currentRoute?.name === name &&
      this.#currentRoute?.param === param
    ) {
      return;
    }

    const prevRoute = this.#currentRoute;

    this.#currentRoute = {
      name,
      param,
    };
    this.#routeStack.splice(0, this.#routeStack.length);

    this.emit("routeChanged", prevRoute, this.#currentRoute, "set");
  }

  replaceRoute<K extends keyof PickProperties<Routes, void | never>>(name: K);

  replaceRoute<K extends keyof PickOptional<Routes>>(
    name: K,
    param?: Routes[K],
  );

  replaceRoute<K extends keyof OmitProperties<Routes, void | never>>(
    name: K,
    param: Routes[K],
  );

  /**
   * Replaces the current route without changing the stack.
   */
  replaceRoute<K extends keyof Routes>(name: K, param?: Routes[K]) {
    if (
      this.#currentRoute?.name === name &&
      this.#currentRoute?.param === param
    ) {
      return;
    }

    const prevRoute = this.#currentRoute;
    this.#currentRoute = {
      name,
      param,
    };

    this.emit("routeChanged", prevRoute, this.#currentRoute, "replace");
    this.emit("replaceRoute", prevRoute, this.#currentRoute);
  }

  pushRoute<K extends keyof PickProperties<Routes, void | never>>(name: K);

  pushRoute<K extends keyof PickOptional<Routes>>(name: K, param?: Routes[K]);

  pushRoute<K extends keyof OmitProperties<Routes, void | never>>(
    name: K,
    param: Routes[K],
  );

  /**
   * Pushes the current route to the stack and sets the current route to the new
   * route.
   */
  pushRoute<K extends keyof Routes>(name: K, param?: Routes[K]) {
    const prevRoute = this.#currentRoute;

    if (this.#currentRoute) {
      this.#routeStack.push(this.#currentRoute);
    }

    this.#currentRoute = {
      name,
      param,
    };

    this.emit("routeChanged", prevRoute, this.#currentRoute, "push");
    this.emit("pushRoute", this.#currentRoute);
  }

  /**
   * Pops the previous route from the stack and sets it as the current route.
   */
  popRoute() {
    const prevRoute = this.#currentRoute;

    const nextRoute = this.#routeStack.pop() ?? null;

    if (nextRoute === null) {
      devError(
        "[modal-navigation] Cannot pop route when there are no routes on the stack",
      );

      return;
    }

    this.#currentRoute = nextRoute;

    if (this.#currentRoute === prevRoute) {
      return;
    }

    this.emit("routeChanged", prevRoute, this.#currentRoute, "pop");
    this.emit("popRoute", this.#currentRoute);
  }
}

export type ModalNavigation<Routes extends RouteMap> = {
  name: string;

  context: React.Context<ModalNavigationRouter<Routes>>;

  Navigator: React.FC<ModalNavigatorProps<Routes>>;
  Route: React.FC<ModalRouteProps<Routes>>;
};

export function createModalNavigation<const Routes extends RouteMap>(
  name: string,
): ModalNavigation<Routes> {
  const context = createContext<ModalNavigationRouter<Routes>>(null);

  return {
    name,

    context,

    Navigator: (props: ModalNavigatorProps<Routes>) => {
      return (
        <context.Provider value={props.modalRouter}>
          <ModalNavigator {...props} />
        </context.Provider>
      );
    },

    Route: ModalRoute,
  };
}

type InitialRouteEntry<T extends RouteMap> = {
  [K in keyof T]: T[K] extends void | never
    ? K | [K]
    : K extends keyof PickOptional<T>
      ? K | [K, T[K]]
      : [K, T[K]];
}[keyof T];

/**
 * Note: It's important to only call this function if the modal navigation is actively
 * being used. This is because it creates a history entry that we need to clean up
 * when the navigation is no longer being used.
 */
export function useModalNavigation<Routes extends RouteMap>(
  nav: ModalNavigation<Routes>,
  initialRoute: InitialRouteEntry<Routes>,
): ModalNavigationRouter<Routes> {
  const [router, __] = useState<ModalNavigationRouter<Routes>>(() => {
    const initialRouteObj: RouteEntry<Routes> = Array.isArray(initialRoute)
      ? {
          name: initialRoute[0],
          param: initialRoute[1],
        }
      : {
          name: initialRoute as PrimitiveKey,
          param: undefined,
        };

    return new ModalNavigationRouter(nav.name, initialRouteObj);
  });

  return router;
}

export function useModalRouter<Routes extends RouteMap>(
  navigation: ModalNavigation<Routes>,
): ModalNavigationRouter<Routes> {
  return useContext(navigation.context);
}

export function useModalRoute<
  const Routes extends RouteMap,
  const Route extends keyof Routes,
>(
  navigation: ModalNavigation<Routes>,
  route: Route,
): [ModalNavigationRouter<Routes>, Routes[Route]] {
  const router = useContext(navigation.context);

  const currentRoute = router.currentRoute;

  if (currentRoute?.name !== route) {
    throw new Error(
      `Expected route "${route.toString()}", but got "${currentRoute.name.toString()}"`,
    );
  }

  return [router, currentRoute.param as Routes[Route]];
}

type ModalNavigatorProps<Routes extends RouteMap> = {
  modalRouter: ModalNavigationRouter<Routes>;

  onPopScope: () => void;

  children:
    | React.ReactElement<ModalRouteProps<Routes>>[]
    | React.ReactElement<ModalRouteProps<Routes>>;
};

function ModalNavigator<Routes extends RouteMap>({
  modalRouter,
  onPopScope,
  children,
}: ModalNavigatorProps<Routes>) {
  const pages = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (child?.type !== ModalRoute) {
        devWarn('ModalNavigation only accepts children of type "ModalRoute"');

        return;
      }

      return child;
    });
  }, [children]);

  const contentRef = useInertTransition();

  const [currentRoute, setCurrentRoute] = useState(
    () => modalRouter.currentRoute,
  );

  const [routingDirection, setRoutingDirection] =
    useState<RoutingDirection>("set");

  const scope = useSharedScope({
    name: modalRouter.name,
    onPop: useCallback(() => {
      // If they've pressed back and we have no more routes on the stack, then notify
      // the parent the user pressed back. This almost certainly means the user is trying to
      // close the modal.
      if (modalRouter.routeStack.length === 0) {
        devDebug('[modal-navigation] Scope popped for "%s"', modalRouter.name);

        onPopScope();
      } else {
        modalRouter.popRoute();
      }
    }, [modalRouter, onPopScope]),
  });

  useEffect(() => {
    function onRouteChanged(
      _prev: RouteEntry<Routes>,
      next: RouteEntry<Routes>,
      direction: RoutingDirection,
    ) {
      devDebug('[modal-navigation] Route changed for "%s"', modalRouter.name);

      setCurrentRoute(next);
      setRoutingDirection(direction);

      if (direction === "push") {
        // Only claim an additional scope if a new route has been pushed
        scope.claim();
      } else if (direction === "set") {
        // If the stack has been cleared, reset and claim a scope.
        scope.clear();
        scope.claim();
      }
    }

    // Whenever we appear on screen, claim a scope so we can handle back navigation.
    scope.claim();

    modalRouter.on("routeChanged", onRouteChanged);

    return () => {
      modalRouter.off("routeChanged", onRouteChanged);
    };
  }, [modalRouter, scope]);

  const currentPage = useMemo(() => {
    return pages.find((page) => page.props.name === currentRoute?.name);
  }, [pages, currentRoute]);

  return (
    <Navigator className={`dir-${routingDirection}`}>
      <div key={currentRoute?.toString()} ref={contentRef}>
        {currentPage}
      </div>
    </Navigator>
  );
}

const Navigator = styled("div")`
  --route-bg: var(--dialog-bg, transparent);

  position: relative;

  display: block;

  & > div {
    background: var(--route-bg);

    transition: var(--transition);
    transition-property: opacity, transform;
  }

  .enter-from {
    opacity: 0;

    transform: scale(0.9);
  }

  .enter-to {
    opacity: 1;

    transform: scale(1);
  }

  .leave {
    position: absolute;
    inset: 0;

    overflow: hidden;
  }

  .leave-from {
    opacity: 1;
  }

  .leave-to {
    opacity: 0;
  }

  &.dir-push .leave-from {
    transform: translateX(0);
  }

  &.dir-push .leave-to {
    transform: translateX(-50%);
  }

  &.dir-pop .leave-from {
    transform: translateX(0);
  }

  &.dir-pop .leave-to {
    transform: translateX(50%);
  }
`;

type ModalRouteProps<Routes extends RouteMap> = {
  name: keyof Routes;

  children: React.ReactNode;
};

function ModalRoute<Routes extends RouteMap>({
  children,
}: ModalRouteProps<Routes>) {
  return children;
}
